import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import ItemList from 'flarum/common/utils/ItemList';
import HashtagCloud from './components/HashtagCloud';
import IndexPage from 'flarum/forum/components/IndexPage';
import Button from 'flarum/common/components/Button';
import Icon from 'flarum/common/components/Icon';

/**
 * fof/forum-widgets-core's side section, as it names its own item.
 */
const FOF_SIDE_ITEM = 'endWidgetSection';

/**
 * The right rail: the About card, and room for anything else.
 *
 * `.Page-container` is already a flex row of sidebar + content, so a third
 * child needs no layout surgery — only a width, which shell/layout.less
 * supplies.
 */
export default function addRightRail() {
  extend('flarum/forum/components/PageStructure', 'containerItems', function (items) {
    if (!app.current || !app.current.matches(IndexPage)) return;

    /*
     * fof/forum-widgets-core adds its side section to this very list, at
     * priority 1 — just below Warren's rail. Left alone that is a FOURTH
     * column in a three-column layout: the row overflows and the feed is
     * squeezed. Adopting the item into the rail gives the forum one right-hand
     * column with both sets of widgets stacked in it, which is what an
     * operator who configured side widgets actually wants.
     */
    let sideWidgets = null;
    if (items.has(FOF_SIDE_ITEM)) {
      sideWidgets = items.get(FOF_SIDE_ITEM);
      items.remove(FOF_SIDE_ITEM);
    }
    const widgets = widgetItems().toArray();

    // An empty 316px column pushes the feed off centre for nothing, and the
    // feed centres perfectly well without it.
    if (!widgets.length && !sideWidgets) return;

    // Priority below 'content' (10) so it lands after the feed column.
    items.add('warrenRail', m("aside", {
      className: "Warren-rail"
    }, widgets, sideWidgets), 5);
  });
}
export function widgetItems() {
  const items = new ItemList();

  /*
   * 🚨 CALLED, not rendered as <AboutCard />.
   *
   * Mithril treats a bare function tag as a CLOSURE component: it calls the
   * function once and expects an object with a `view` method back. A function
   * that returns a vnode instead hands Mithril something with no `view`, and
   * the next redraw dies inside render.js on `undefined.apply` — which takes
   * the whole page down, not just the rail. The discussion list spins forever
   * and nothing in the error names this file.
   *
   * Either shape is fine; a plain function has to be invoked.
   */
  // Off means off: the rail renders nothing rather than an empty panel, and
  // the column below is dropped entirely when it has no widgets.
  if (app.forum.attribute('warrenShowAbout') !== false) {
    items.add('about', aboutCard(), 100);
  }

  /*
   * 🚨 Gated on the hashtags extension being ENABLED, not on the route
   * existing or the request succeeding.
   *
   * Without the gate every forum without it pays a request that 404s on every
   * page load, and the reader briefly sees a loading panel for a feature the
   * forum does not have. The server tells us, because the frontend cannot see
   * the extension list for itself.
   */
  if (app.forum.attribute('warrenHasHashtags')) {
    items.add('hashtags', m(HashtagCloud, null), 80);
  }
  return items;
}
function aboutCard() {
  const forum = app.forum;
  const description = forum.attribute('description');
  return m("div", {
    className: "Warren-card Warren-card--about"
  }, m("div", {
    className: "Warren-card-head Warren-card-head--accent"
  }, app.translator.trans('ernestdefoe-warren.forum.rail.about')), m("div", {
    className: "Warren-card-body"
  }, description ? m("p", {
    className: "Warren-about-text"
  }, description) : null, m("div", {
    className: "Warren-card-stat"
  }, m("span", null, app.translator.trans('ernestdefoe-warren.forum.rail.members')), m("span", null, formatCount(forum.attribute('warrenMemberCount')))), m("div", {
    className: "Warren-card-stat"
  }, m("span", null, app.translator.trans('ernestdefoe-warren.forum.rail.discussions')), m("span", null, formatCount(forum.attribute('warrenDiscussionCount')))), m("div", {
    className: "Warren-card-stat"
  }, m("span", null, app.translator.trans('ernestdefoe-warren.forum.rail.comments')), m("span", null, formatCount(forum.attribute('warrenPostCount')))), app.forum.attribute('canStartDiscussion') ? m(Button, {
    className: "Button Button--primary Warren-about-cta",
    onclick: () => app.composer.load(() => flarum.reg.asyncModuleImport('flarum/forum/components/DiscussionComposer'), {
      user: app.session.user
    })
  }, m(Icon, {
    name: "fas fa-plus"
  }), app.translator.trans('ernestdefoe-warren.forum.rail.create')) : null));
}
function formatCount(value) {
  const n = Number(value || 0);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}m`;
  if (n >= 10000) return `${(n / 1000).toFixed(0)}k`;
  return n.toLocaleString();
}